import React, { useEffect } from 'react';
import { Card, CardBodyText, CardContent } from '@saucelabs/ds-card';
import { Typography } from '@saucelabs/ds-typography/lib';
import { useDispatch } from 'react-redux';
import { Button } from '@saucelabs/ds-button';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';
import { amountSeparator } from '@saucelabs/sl-util-helpers/lib/numberHelpers';

import { PRODUCT_USAGE, THREEDS_AUTH_ERROR_TYPE, THREEDS_FAILURE_MESSAGE } from '../../../constants';
import { orderItemsToSubscriptionPreviews } from '../../../ducks/plans/utils';
import { PlanConfiguration } from '../../../records/PlanConfiguration';
import { PRODUCT_AREA, PRODUCT_SUB_AREAS, SUBJECT } from '../../../tracking';
import { handle3DSecure } from '../../../utils/threeDSecure';
import { handleCheckoutSuccess } from '../../../ducks/checkout/operations';
import { OrderSummaryHeader, OrderSummarySeparator } from './OrderSummaryComponents';
import { OrderedPlans } from './OrderedPlans';

interface OrderPriceProps {
  discount: number;
  tax: number
  total: number;
}

export const OrderPrice: React.FC<OrderPriceProps> = ({ discount, tax, total }) => (
  <div>
    <div
      className="flex flex-row justify-between mt-2"
    >
      <Typography
        className="text-high-emphasis"
        disableSpacing
        variant="body"
      >
        Taxes and fees
      </Typography>
      <Typography
        className="text-high-emphasis"
        disableSpacing
        variant="body"
      >
        {`$${tax}`}
      </Typography>
    </div>
    { discount > 0 ? (
      <div
        className="flex flex-row justify-between mb-2"
      >

        <Typography
          className="text-high-emphasis"
          disableSpacing
          variant="body"
        >
          Coupon Code
        </Typography>
        <Typography
          className="text-green-600"
          disableSpacing
          variant="body"
        >
          {`-$${discount}`}
        </Typography>

      </div>
    ) : <div className="mb-2" />}
    <OrderSummarySeparator />
    <div className="flex flex-row justify-between mt-2">
      <Typography
        className="text-high-emphasis mt-0 mb-0"
        disableSpacing
        variant="heading-4"
      >
        Total to Pay
      </Typography>
      <div className="flex flex-col items-end">
        <Typography
          className="text-high-emphasis mt-0 mb-0"
          disableSpacing
          variant="heading-4"
        >
          {amountSeparator(total)}
        </Typography>
      </div>
    </div>
  </div>
);

interface CheckoutPreview {
  balance: number,
  discount: number,
  subtotal: number,
  total: number,
  tax: number,
}

interface OrderSummaryFromPreviewProps {
  activeSubscriptions,
  allPlans: Array<Plan>;
  checkoutError;
  checkoutPreview: CheckoutPreview;
  checkoutPurchase;
  clearNotifications,
  clearPurchaseData;
  fetchCheckoutPreview,
  orderItems: Map<string, PlanConfiguration>;
  user,
}

const CreditsTopupInfo: React.FC = () => (
  <Typography
    className="mt-2 text-medium-emphasis"
    component="span"
    variant="body-small"
  >
    When you have fewer than 1,000 credits remaining within a billing cycle,
    25,000 credits will automatically be added to your account so you never
    run out of test capacity. You’ll be charged $50 plus taxes and fees.

  </Typography>
);

export const OrderSummaryFromPreview: React.FC<OrderSummaryFromPreviewProps> = ({
  allPlans,
  checkoutPreview,
  checkoutPurchase,
  orderItems,
  clearPurchaseData,
  fetchCheckoutPreview,
  activeSubscriptions,
  clearNotifications,
  user,
  checkoutError,
}) => {
  let cleanup3DSecureListeners;
  const productTypes = orderItems.keySeq().toArray();
  const usageOrdered = productTypes.includes(PRODUCT_USAGE);

  useEffect(() => {
    fetchCheckoutPreview();

    if (cleanup3DSecureListeners) {
      cleanup3DSecureListeners();
    }
  }, []);

  const [isPurchasing, setIsPurchasing] = React.useState(false);
  const subscriptionPreviews = orderItemsToSubscriptionPreviews(orderItems, allPlans);

  const paymentVerificationElementRef = React.createRef();
  const isUserFree = user?.isFreeUser();

  const dispatch = useDispatch();
  const onError = (message) => {
    trackStandardEvent(
      STANDARD_EVENTS.SERVER_RESPONDED,
      PRODUCT_AREA,
      PRODUCT_SUB_AREAS.CHECKOUT,
      SUBJECT.ORDER_FAILED,
      { message },
    );
    setIsPurchasing(false);
    checkoutError(message);
  };

  const doPurchase = ({ threeDsToken = null }) => {
    clearNotifications();
    setIsPurchasing(true);
    checkoutPurchase({
      threeDsTokenId: threeDsToken?.id,
    })
      .then(({ orderItems: checkoutOrderItems, paymentInfo }) => {
        trackStandardEvent(
          STANDARD_EVENTS.SERVER_RESPONDED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CHECKOUT,
          SUBJECT.ORDER_SUCCESS,
        );
        clearPurchaseData();
        return dispatch(handleCheckoutSuccess({
          activeSubscriptions,
          isUserFree,
          orderItems: checkoutOrderItems,
          paymentInfo,
        }));
      })
      .catch((error) => {
        const { response } = error;
        if (response?.data?.detail?.three_d_secure_action_token_id) {
          const { remove3DListeners } = handle3DSecure({
            onError: (innerError) => {
              const message = innerError.code === THREEDS_AUTH_ERROR_TYPE ?
                THREEDS_FAILURE_MESSAGE :
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `Error processing payment verification: ${innerError.message}`;

              onError(message);
            },
            onVerified: doPurchase,
            response,
            verificationElement: paymentVerificationElementRef.current,
          });
          cleanup3DSecureListeners = remove3DListeners;
        } else {
          let { message } = error;
          // show error detail from recurly otherwise show error code
          if (response?.data && response.status === 422) {
            const { detail } = response.data;
            message = detail || message;
            if (Array.isArray(detail)) {
              // validation error due to user waiting for too long. Retry the whole operation.
              // We ignore the current token's value. This operation will generate a new one.
              if (detail[0]?.param === 'transaction.amount') {
                fetchCheckoutPreview().then(doPurchase);
                return;
              }
              onError(detail[0].message);
            } else {
              onError(message);
            }
          } else {
            onError('Purchase request failed, please try again');
          }
        }
      });
  };

  const OrderPurchase: React.FC = () => (
    <Button
      className="flex justify-center w-full h-6"
      color="primary"
      disabled={isPurchasing}
      onPress={doPurchase}
    >
      Purchase Now
    </Button>
  );

  return (
    <>
      <Card className="w-full">
        <CardContent className="p-0.5">
          <CardBodyText>
            <OrderSummaryHeader />
            <OrderSummarySeparator />
            <OrderedPlans
              allPlans={allPlans}
              orderItems={orderItems}
              subscriptionPreviews={subscriptionPreviews}
            />
            <div>
              <OrderPrice
                discount={checkoutPreview.discount}
                tax={checkoutPreview.tax}
                total={checkoutPreview.total}
              />
              <div className="mt-2">
                <OrderPurchase />
              </div>
            </div>
          </CardBodyText>
        </CardContent>
      </Card>
      {usageOrdered && <CreditsTopupInfo />}
    </>
  );
};
